import React, { useMemo, useState } from "react";
import styled from "styled-components";
import * as Icons from "@ui/components/icons";
import { Section, SectionIntro, SectionTitle } from "./debugUi";

type IconComponent = React.ComponentType;

const iconEntries = (Object.entries(Icons) as [string, unknown][]).filter(
	(entry): entry is [string, IconComponent] => typeof entry[1] === "function"
);

export const IconsSection: React.FC = () => {
	const [query, setQuery] = useState("");
	const visible = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return iconEntries;
		return iconEntries.filter(([name]) => name.toLowerCase().includes(needle));
	}, [query]);

	return (
		<Section id="icons">
			<SectionTitle>Icônes</SectionTitle>
			<SectionIntro>
				{iconEntries.length} exports de icons.tsx. {visible.length} affichés.
			</SectionIntro>
			<Search value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filtrer par nom" />
			<Grid>
				{visible.map(([name, Icon]) => (
					<IconErrorBoundary key={name} name={name}>
						<Tile>
							<Preview>
								<Icon />
							</Preview>
							<Name>{name}</Name>
						</Tile>
					</IconErrorBoundary>
				))}
			</Grid>
		</Section>
	);
};

class IconErrorBoundary extends React.Component<{ name: string; children: React.ReactNode }, { failed: boolean }> {
	state = { failed: false };

	static getDerivedStateFromError(): { failed: boolean } {
		return { failed: true };
	}

	render(): React.ReactNode {
		if (this.state.failed) {
			return (
				<Tile>
					<Preview>props requises</Preview>
					<Name>{this.props.name}</Name>
				</Tile>
			);
		}
		return this.props.children;
	}
}

const Search = styled.input`
	height: 40px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	padding: 0 12px;
	background: ${({ theme }) => theme.colors.white};
	color: ${({ theme }) => theme.colors.textPrimary};
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
	gap: 10px;
`;

const Tile = styled.article`
	background: ${({ theme }) => theme.colors.white};
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 10px;
	padding: 10px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-height: 96px;
`;

const Preview = styled.div`
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	background-color: #e7ebf0;
	background-image:
		linear-gradient(45deg, #c5ced8 25%, transparent 25%), linear-gradient(-45deg, #c5ced8 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, #c5ced8 75%), linear-gradient(-45deg, transparent 75%, #c5ced8 75%);
	background-size: 12px 12px;
	background-position:
		0 0,
		0 6px,
		6px -6px,
		-6px 0;
	overflow: hidden;
	color: ${({ theme }) => theme.colors.textSecondary};
	font-size: 0.7rem;

	svg {
		max-width: 36px;
		max-height: 36px;
		width: auto;
		height: auto;
	}
`;

const Name = styled.code`
	font-size: 0.68rem;
	word-break: break-word;
	color: ${({ theme }) => theme.colors.textPrimary};
`;
